import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../services/api';

/**
 * Interface representing a page object returned from the API
 */
export interface UserPage {
    id: string;
    name: string;           // Required for routing/navigation
    display_name: string;   // Shown in page header
    sidebar_name: string;   // Shown in sidebar navigation
    description: string;    // Page description shown in header
    sort_order: number;     // Display order in sidebar
    show_marquee?: boolean;
    marquee_text?: string | null;
}

/**
 * Interface representing the return value of the useUserPages hook
 */
export interface UseUserPagesReturn {
    pages: UserPage[];
    loading: boolean;
    error: Error | null;
    refetch: () => Promise<any>;
}

export const useUserPages = (): UseUserPagesReturn => {
    const { token, isAuthenticated } = useAuth();

    const {
        data: pages = [],
        isLoading,
        error,
        refetch
    } = useQuery({
        queryKey: ['user-pages', token],
        queryFn: async () => {
            if (!token) return [];
            
            const response = await api.get('/user/pages', {
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
            });

            const rawPages: UserPage[] = response.data.map((page: any) => ({
                ...page,
                description: page.description || (PageDescription as any)[page.name] || '',
                show_marquee: page.show_marquee === true,
                marquee_text: page.marquee_text ?? null,
            }));

            // Sort logic: use sort_order from API, or FALLBACK_ORDER if undefined
            return [...rawPages].sort((a, b) => {
                const orderA = a.sort_order ?? (FALLBACK_ORDER as any)[a.name] ?? 999;
                const orderB = b.sort_order ?? (FALLBACK_ORDER as any)[b.name] ?? 999;
                return orderA - orderB;
            });
        },
        enabled: isAuthenticated && !!token,
        staleTime: 2 * 60 * 1000,  // Refresh marquee/settings within 2 minutes
        gcTime: 30 * 60 * 1000,
        refetchOnWindowFocus: true,
        retry: 1,
    });

    return {
        pages,
        loading: isLoading,
        error: error as Error | null,
        refetch
    };
};

/**
 * Fallback display order for pages if sort_order is not provided by backend
 */
const FALLBACK_ORDER = {
    'dashboard': 1,
    'my-insights': 2,
    'my-team': 3,
    'projects': 4,
    'projects-portfolio': 5,
    'ai-chatbot': 5.5,
    'squad-structure': 6,
    'customer-feedback': 7,
    'dri-insights': 8,
    'people-analytics': 9,
    'forms': 10,
    'admin': 100
};

/**
 * Local mapping for page descriptions until fully managed by backend
 */
const PageDescription = {
    'dashboard': 'Your resource optimization command center. Monitor capacity and solver productivity across your assigned BU/SBUs with real-time visibility. Track allocated hours (SFDC) against actual time logged (Zoho Timesheets) to identify over/under-utilization. View solver distribution across projects versus bench to maximize billable utilization.',
    'projects': 'Your project portfolio command center. Access real-time analytics across projects in your assigned BU/SBUs. Monitor portfolio metrics, funding sources, timeline and budget health, status, revenue trends, and top accounts to track performance, identify risks early, and drive profitable outcomes.',
    'projects-portfolio': 'Browse a consolidated projects table with core fields and linked solution stories for your assigned BU/SBUs. Filter, sort, and explore portfolio rows aligned with project access.',
    'ai-chatbot': 'Ask questions about portfolio data in natural language. The assistant can return charts, tables, and SQL-backed insights for your assigned projects.',
    'my-insights': 'Your personalized performance dashboard. Monitor your individual utilization by tracking allocated hours (SFDC) against actual time logged (Zoho Projects) to understand your Utilization. Track your project assignments, certifications, skill development, and timesheet compliance. Take control of your productivity and career growth with real-time performance metrics.',
    'my-team': 'Your team capability and performance hub. Track reportees\' utilization, project health, and skill development. Monitor headcount allocation (projects vs. bench), analyze allocated vs. actual hours, and view certifications and DRI scores to optimize capacity, identify skill gaps, and drive talent decisions. Displays your data if no reportees found.',
    'squad-structure': 'Visual map of organization hierarchy, business units, and team structures.',
    'customer-feedback': 'Sentiment analysis and satisfaction scores from client satisfaction surveys.',
    'dri-insights': 'Delivery Readiness Index (DRI) trends, certification tracking, and skill badge progress.',
    'people-analytics': 'In-depth analysis of talent density, tenure, and recruitment pipelines.',
    'forms': 'Internal compliance forms, survey submissions, and data collection tools.',
    'admin': 'System configuration, role-based access control, and user management.'
};
